import { message } from "antd";
import {
    fetchPerson,
    searchPeople,
    fetchPeopleListsV2,
    createPeopleListV2,
    addPeopleToListV2,
    fetchPeopleFromProductList,
    fetchPeopleFromProducts,
} from "./service";
import { validateCredits } from "@/utils/credits";
import { Effect, Reducer, AnyAction } from '@umijs/max';

export interface PersonSearchRequest {
    page?: number;
    role_title?: string;
    person_locations?: number[];
    person_name?: string;
    organizations?: number[];
    organization_employees_start?: number;
    organization_employees_end?: number;
    organization_revenue_start?: number;
    organization_revenue_end?: number;
    organization_industries?: number[];
    role_is_current?: boolean;
    person_skills?: number[];
    funding_types?: string[];
    funding_total_start?: number;
    funding_total_end?: number;
}

export interface SimplifiedPerson {
    record_id: number;
    person_id: number;
    person_photo: string | null;
    person_gender: string | null;
    name: string;
    role_title: string;
    location: string | null;
    linkedin_url: string | null;
    organization_id: number;
    organization_name: string;
    organization_legal_name: string;
    organization_logo: string | null;
    organization_employees_range: string | null;
    organization_employees_amount: number | null;
    organization_domain: string | null;
    viewed?: boolean;
    emails: string[] | string | null;
    work_email: string | null;
    work_email_status: string | null;
    date_updated: string | null;
    email_fetched_from: string | null;
}

export interface SimplifiedPersonSearchResponse {
    search_results: SimplifiedPerson[];
    total_search_results: number;
}

interface PeopleModelState {
    searchValue: string;
    filters: Partial<PersonSearchRequest>;
    peopleData: {
        pages: {
            [key: number]: {
                data: SimplifiedPerson[];
                total: number;
            }
        };
        total: number;
    };
    currentPage: number;
    pageSize: number;
    selectedRows: number[];
    filterDrawerVisible: boolean;
    peopleLists: any[];
    listModalVisible: boolean;
    createListModalVisible: boolean;
    addingToList: boolean;
    organizationOptions: Array<{ value: string, label: string }>;
}

interface PeopleModelType {
    namespace: 'people';
    state: PeopleModelState;
    effects: {
        fetchPeople: Effect;
        fetchPerson: Effect;
        fetchPeopleListsV2: Effect;
        createPeopleList: Effect;
        addPeopleToList: Effect;
        addAllPeopleToSelectedRows: Effect;
        getSelectedPeopleFromIds: Effect;
        fetchPeopleFromProductList: Effect;
        fetchPeopleFromProducts: Effect;
        changePage: Effect;
    };
    reducers: {
        updateState: Reducer<PeopleModelState>;
        updateSearchValue: Reducer<PeopleModelState>;
        updateSearchValueOnly: Reducer<PeopleModelState>;
        setPersonsForPage: Reducer<PeopleModelState>;
        resetFilters: Reducer<PeopleModelState>;
        toggleFilterDrawer: Reducer<PeopleModelState>;
        updateFilter: Reducer<PeopleModelState>;
        addToSelectedRows: Reducer<PeopleModelState>;
        removeFromSelectedRows: Reducer<PeopleModelState>;
        clearSelectedRows: Reducer<PeopleModelState>;
        setPeopleLists: Reducer<PeopleModelState>;
        toggleListModal: Reducer<PeopleModelState>;
        toggleCreateListModal: Reducer<PeopleModelState>;
        setAddingToList: Reducer<PeopleModelState>;
        resetAll: Reducer<PeopleModelState>;
    };
}

const Model: PeopleModelType = {
    namespace: 'people',
    state: {
        searchValue: '',
        filters: {},
        peopleData: {
            pages: {},
            total: 0
        },
        currentPage: 1,
        pageSize: 10,
        selectedRows: [],
        filterDrawerVisible: false,
        peopleLists: [],
        listModalVisible: false,
        createListModalVisible: false,
        addingToList: false,
        organizationOptions: [],
    },
    effects: {
        *fetchPeople({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.people);

                const current = 1;

                const searchParams: PersonSearchRequest = {
                    ...state.filters,
                    ...(payload as Partial<PersonSearchRequest>),
                    page: current,
                    person_name: state.searchValue || undefined
                };

                // Modified check: ignore role_is_current when checking if all params are empty
                const hasValidParams = Object.entries(searchParams).some(([key, value]) => {
                    if (key === 'role_is_current' || key === 'page' || key === '_timestamp') return false;
                    if (Array.isArray(value) && value.length > 0) return true;
                    if (value !== undefined && value !== null && value !== '') return true;
                    return false;
                });
                
                if (!hasValidParams) {
                    message.error('Please provide search terms or apply filters before searching.');
                    return;
                }

                // Reset peopleData before fetching new results
                yield put({
                    type: 'updateState',
                    payload: {
                        peopleData: {
                            pages: {},
                            total: 0
                        }
                    }
                });

                yield put({
                    type: 'clearSelectedRows'
                });

                const response: SimplifiedPersonSearchResponse = yield call(searchPeople, searchParams);
                yield put({
                    type: 'setPersonsForPage',
                    payload: {
                        page: current,
                        data: response.search_results,
                        total: response.total_search_results
                    }
                });

                yield put({
                    type: 'updateState',
                    payload: {
                        currentPage: current
                    }
                });
            } catch (error) {
                message.error('Failed to fetch people data');
                console.error('Error fetching people:', error);
            }
        },
        *changePage({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.people);
                const targetPage = payload.page;

                // Check if we already have data for this page
                if (state.peopleData.pages[targetPage]?.data?.length > 0) {
                    // If we're just navigating to a page we already have data for, update the current page
                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: targetPage
                        }
                    });
                    return;
                }

                // We don't have data for this page, so make an API call
                const searchParams: PersonSearchRequest = {
                    ...state.filters,
                    page: targetPage, // Backend uses 0-based indexing
                    person_name: state.searchValue || undefined
                };

                const response: SimplifiedPersonSearchResponse = yield call(searchPeople, searchParams);

                yield put({
                    type: 'setPersonsForPage',
                    payload: {
                        page: targetPage,
                        data: response.search_results,
                        total: response.total_search_results
                    }
                });

                yield put({
                    type: 'updateState',
                    payload: {
                        currentPage: targetPage
                    }
                });
            } catch (error) {
                message.error('Failed to fetch people data for page');
                console.error('Error fetching people for page:', error);
            }
        },
        *fetchPerson({ callback, payload, isFreePlan }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                let state = yield select((state: any) => state.people);
                const { peopleData } = state;

                const isViewed = Object.values(peopleData.pages).some((pageData: any) => {
                    return pageData.data.some((person: SimplifiedPerson) =>
                        person.person_id === payload && person.viewed
                    );
                });

                if (isViewed) {
                    callback?.();
                    return;
                }

                const { people_credits_remaining } = yield select((state: any) => state.subscription);
                if (!validateCredits(people_credits_remaining, 'person')) {
                    callback?.();
                    return;
                }

                const response: SimplifiedPerson = yield call(fetchPerson, payload, isFreePlan);

                state = yield select((state: any) => state.people);
                const { currentPage } = state;
                const pageData = peopleData.pages[currentPage]?.data || [];

                const updatedPageData = pageData.map((person: SimplifiedPerson) => {
                    if (person.person_id === response.person_id) {
                        return {
                            ...response,
                            viewed: true
                        };
                    }
                    return person;
                });

                yield put({
                    type: 'setPersonsForPage',
                    payload: {
                        page: currentPage,
                        data: updatedPageData,
                        total: peopleData.total
                    }
                });

                callback?.();

                yield put({
                    type: 'subscription/deductCredits',
                    payload: {
                        people_credits_remaining: people_credits_remaining - 1
                    }
                });


                // DEBUG ONLY:
                if(response.work_email) {
                    // message.info('Fetched from: ' + response.email_fetched_from);
                }


            } catch (error) {
                message.error('Failed to fetch person data');
                console.error('Error fetching person:', error);
            }
        },
        *fetchPeopleListsV2(_: AnyAction, { call, put }: any): Generator<any, void, any> {
            try {
                const response: any[] = yield call(fetchPeopleListsV2);

                yield put({
                    type: 'setPeopleLists',
                    payload: response
                });
            } catch (error) {
                message.error('Failed to fetch people lists');
                console.error('Error fetching people lists:', error);
            }
        },
        *createPeopleList({ payload, callback }: AnyAction, { call, put }: any): Generator<any, void, any> {
            try {
                const response: any = yield call(createPeopleListV2, { name: payload.name });

                yield put({
                    type: 'fetchPeopleListsV2'
                });

                if (callback) {
                    callback(response);
                }

                yield put({
                    type: 'toggleCreateListModal',
                    payload: false
                });

                message.success('List created successfully');
            } catch (error) {
                message.error('Failed to create list');
                console.error('Error creating list:', error);
            }
        },
        *addPeopleToList({ payload, callback }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                yield put({
                    type: 'setAddingToList',
                    payload: true
                });

                const response: any = yield call(addPeopleToListV2, {
                    listId: payload.listId,
                    peopleIds: payload.peopleIds
                });

                if (response.credits_to_deduct > 0) {
                    yield put({
                        type: 'subscription/deductCredits',
                        payload: {
                            people_credits_remaining: (yield select((state: any) =>
                                state.subscription.people_credits_remaining
                            )) - response.credits_to_deduct
                        }
                    });
                }

                // Get people IDs from response
                const peopleFromResponse = response.people || [];
                const updatedPeopleIds = peopleFromResponse.map((p: SimplifiedPerson) => p.person_id);
                
                // Get all the peopleData
                const peopleData = yield select((state: any) => state.people.peopleData);
                
                // Create deep copy of pages to safely modify
                const updatedPages = JSON.parse(JSON.stringify(peopleData.pages));
                
                // Update viewed status for each person across all pages
                Object.keys(updatedPages).forEach(pageKey => {
                    const page = updatedPages[pageKey];
                    if (page && page.data) {
                        updatedPages[pageKey].data = page.data.map((person: SimplifiedPerson) => {
                            if (updatedPeopleIds.includes(person.person_id)) {
                                // Find this person in the response data for complete information
                                const updatedPerson = peopleFromResponse.find(
                                    (p: SimplifiedPerson) => p.person_id === person.person_id
                                );
                                
                                // Merge with response data if available, always set viewed: true
                                return {
                                    ...person,
                                    ...(updatedPerson || {}),
                                    viewed: true
                                };
                            }
                            return person;
                        });
                    }
                });
                
                // Update state with modified data
                yield put({
                    type: 'updateState',
                    payload: {
                        peopleData: {
                            ...peopleData,
                            pages: updatedPages
                        }
                    }
                });

                yield put({
                    type: 'toggleListModal',
                    payload: false
                });

                yield put({
                    type: 'clearSelectedRows'
                });

                yield put({
                    type: 'setAddingToList',
                    payload: false
                });

                if (callback) {
                    callback(response);
                }

                if(response && response.message) {
                    message.success(response.message);
                } else {
                    message.success(`Added ${payload.peopleIds.length} people to list`);
                }
            } catch (error) {
                yield put({
                    type: 'setAddingToList',
                    payload: false
                });

                message.error('Failed to add people to list');
                console.error('Error adding people to list:', error);
            }
        },
        *addAllPeopleToSelectedRows({ payload }: AnyAction, { put, select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.people);
                const allRows: number[] = [];

                Object.values(state.peopleData.pages).forEach((page: any) => {
                    page.data.forEach((person: SimplifiedPerson) => {
                        allRows.push(person.person_id);
                    });
                });

                yield put({
                    type: 'updateState',
                    payload: {
                        selectedRows: [...new Set(allRows)]
                    }
                });
            } catch (error) {
                console.error('Error adding all people to selected rows:', error);
            }
        },
        *getSelectedPeopleFromIds({ callback }: AnyAction, { select }: any): Generator<any, void, any> {
            try {
                const state = yield select((state: any) => state.people);
                const { selectedRows, peopleData } = state;

                // Get all people data from all pages
                const allPeople: SimplifiedPerson[] = [];
                Object.values(peopleData.pages).forEach((page: any) => {
                    page.data.forEach((person: SimplifiedPerson) => {
                        if (selectedRows.includes(person.person_id)) {
                            allPeople.push(person);
                        }
                    });
                });

                if (callback) {
                    callback(allPeople);
                }
            } catch (error) {
                console.error('Error getting selected people:', error);
                if (callback) {
                    callback([]);
                }
            }
        },
        *fetchPeopleFromProductList({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const response: any = yield call(fetchPeopleFromProductList, payload);
                // Extract people and marketers from the response
                const { people, marketers } = response;

                // If we have marketers, directly set the organizations filter
                if (marketers && marketers.length > 0) {
                    // Create organization filter options from marketers
                    const organizationIds = marketers.map((marketer: any) => marketer.forager_id);

                    // Create options for display in the Select component
                    const options = marketers.map((marketer: any) => ({
                        value: marketer.forager_id,
                        label: marketer.name || marketer.domain || marketer.forager_id,
                    }));

                    // Store the organization options
                    yield put({
                        type: 'updateState',
                        payload: {
                            organizationOptions: options
                        }
                    });

                    // Set the organizations filter value
                    yield put({
                        type: 'updateState',
                        payload: {
                            filters: {
                                ...((yield select((state: any) => state.people.filters)) || {}),
                                organizations: organizationIds
                            }
                        }
                    });
                }

                // If we have people data, update the state with it
                if (people && people.search_results.length > 0) {
                    yield put({
                        type: 'setPersonsForPage',
                        payload: {
                            page: 1,
                            data: people.search_results,
                            total: people.total_search_results
                        }
                    });

                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: 1
                        }
                    });
                } else {
                    // If no people data came with the response, fetch people with the updated filters
                    yield put({
                        type: 'fetchPeople',
                        payload: {
                            page: 0
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching people from product list:', error);
            }
        },
        *fetchPeopleFromProducts({ payload }: AnyAction, { call, put, select }: any): Generator<any, void, any> {
            try {
                const response: any = yield call(fetchPeopleFromProducts, payload);
                // Extract people and marketers from the response
                const { people, marketers } = response;

                // If we have marketers, directly set the organizations filter
                if (marketers && marketers.length > 0) {
                    // Create organization filter options from marketers
                    const organizationIds = marketers.map((marketer: any) => marketer.forager_id);

                    // Create options for display in the Select component
                    const options = marketers.map((marketer: any) => ({
                        value: marketer.forager_id,
                        label: marketer.name || marketer.domain || marketer.forager_id,
                    }));

                    // Store the organization options
                    yield put({
                        type: 'updateState',
                        payload: {
                            organizationOptions: options
                        }
                    });

                    // Set the organizations filter value
                    yield put({
                        type: 'updateState',
                        payload: {
                            filters: {
                                ...((yield select((state: any) => state.people.filters)) || {}),
                                organizations: organizationIds
                            }
                        }
                    });
                }

                // If we have people data, update the state with it
                if (people && people.search_results.length > 0) {
                    yield put({
                        type: 'setPersonsForPage',
                        payload: {
                            page: 1,
                            data: people.search_results,
                            total: people.total_search_results
                        }
                    });

                    yield put({
                        type: 'updateState',
                        payload: {
                            currentPage: 1
                        }
                    });
                } else {
                    // If no people data came with the response, fetch people with the updated filters
                    yield put({
                        type: 'fetchPeople',
                        payload: {
                            page: 0
                        }
                    });
                }
            } catch (error) {
                console.error('Error fetching people from products:', error);
            }
        }
    },
    reducers: {
        updateState(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (Object.keys(payload).every(key => state[key as keyof PeopleModelState] === payload[key])) {
                return state;
            }
            return {
                ...state,
                ...payload
            };
        },
        updateSearchValue(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (state.searchValue === payload) {
                return state;
            }
            return {
                ...state,
                searchValue: payload,
                currentPage: 1,
            };
        },
        updateSearchValueOnly(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (state.searchValue === payload) {
                return state;
            }
            return {
                ...state,
                searchValue: payload,
            };
        },
        setPersonsForPage(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const { page, data, total } = payload;

            return {
                ...state,
                peopleData: {
                    ...state.peopleData,
                    pages: {
                        ...state.peopleData.pages,
                        [page]: {
                            data,
                            total
                        }
                    },
                    total
                }
            };
        },
        resetFilters(state: PeopleModelState): PeopleModelState {
            return {
                ...state,
                filters: {},
                searchValue: '',
                peopleData: {
                    pages: {},
                    total: 0
                },
                currentPage: 1
            };
        },
        toggleFilterDrawer(state: PeopleModelState): PeopleModelState {
            return {
                ...state,
                filterDrawerVisible: !state.filterDrawerVisible
            };
        },
        updateFilter(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const { field, value } = payload;

            if (state.filters[field as keyof Partial<PersonSearchRequest>] === value) {
                return state;
            }

            return {
                ...state,
                filters: {
                    ...state.filters,
                    [field]: value
                }
            };
        },
        addToSelectedRows(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (state.selectedRows.includes(payload)) {
                return state;
            }

            return {
                ...state,
                selectedRows: [...state.selectedRows, payload]
            };
        },
        removeFromSelectedRows(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            if (!state.selectedRows.includes(payload)) {
                return state;
            }

            return {
                ...state,
                selectedRows: state.selectedRows.filter(id => id !== payload)
            };
        },
        clearSelectedRows(state: PeopleModelState): PeopleModelState {
            if (state.selectedRows.length === 0) {
                return state;
            }

            return {
                ...state,
                selectedRows: []
            };
        },
        setPeopleLists(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            return {
                ...state,
                peopleLists: payload
            };
        },
        toggleListModal(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const newValue = payload !== undefined ? payload : !state.listModalVisible;
            return {
                ...state,
                listModalVisible: newValue
            };
        },
        toggleCreateListModal(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            const newValue = payload !== undefined ? payload : !state.createListModalVisible;
            return {
                ...state,
                createListModalVisible: newValue
            };
        },
        setAddingToList(state: PeopleModelState, { payload }: AnyAction): PeopleModelState {
            return {
                ...state,
                addingToList: payload
            };
        },
        resetAll(state: PeopleModelState): PeopleModelState {
            return {
                ...state,
                searchValue: '',
                filters: {},
                peopleData: {
                    pages: {},
                    total: 0
                },
                currentPage: 1,
                pageSize: 10,
                selectedRows: [],
                filterDrawerVisible: false,
                peopleLists: [],
                listModalVisible: false,
                createListModalVisible: false,
                addingToList: false,
                organizationOptions: [],
            }
        }
    }
}

export default Model;